import { unescapeHTML } from '../utils/html';

import api, { getLinks } from '../api';

import {
  importFetchedAccounts,
  importFetchedStatuses,
  importFetchedStatus,
} from './importer';

export const CONVERSATIONS_MOUNT   = 'CONVERSATIONS_MOUNT';
export const CONVERSATIONS_UNMOUNT = 'CONVERSATIONS_UNMOUNT';

export const CONVERSATIONS_FETCH_REQUEST = 'CONVERSATIONS_FETCH_REQUEST';
export const CONVERSATIONS_FETCH_SUCCESS = 'CONVERSATIONS_FETCH_SUCCESS';
export const CONVERSATIONS_FETCH_FAIL    = 'CONVERSATIONS_FETCH_FAIL';
export const CONVERSATIONS_UPDATE        = 'CONVERSATIONS_UPDATE';

export const CONVERSATIONS_READ = 'CONVERSATIONS_READ';

export const CONVERSATIONS_UNREAD_COUNT = 'CONVERSATIONS_UNREAD_COUNT';

export const CONVERSATIONS_DELETE_REQUEST = 'CONVERSATIONS_DELETE_REQUEST';
export const CONVERSATIONS_DELETE_SUCCESS = 'CONVERSATIONS_DELETE_SUCCESS';
export const CONVERSATIONS_DELETE_FAIL    = 'CONVERSATIONS_DELETE_FAIL';

export const mountConversations = () => ({
  type: CONVERSATIONS_MOUNT,
});

// Fetches the server-side count of unread conversations for the sidebar badge.
export const fetchConversationsUnreadCount = () => (dispatch) => {
  api().get('/api/v1/conversations/unread_count').then(({ data }) => {
    dispatch({
      type: CONVERSATIONS_UNREAD_COUNT,
      count: data.count,
    });
  }).catch(() => {
    // Silent failure: the badge just stays at its previous value.
  });
};

export const unmountConversations = () => ({
  type: CONVERSATIONS_UNMOUNT,
});

export const markConversationRead = conversationId => (dispatch) => {
  dispatch({
    type: CONVERSATIONS_READ,
    id: conversationId,
  });

  api().post(`/api/v1/conversations/${conversationId}/read`);
};

export const expandConversations = ({ maxId } = {}) => (dispatch, getState) => {
  dispatch(expandConversationsRequest());

  const params = { max_id: maxId };

  if (!maxId) {
    params.since_id = getState().getIn(['conversations', 'items', 0, 'last_status']);
  }

  const isLoadingRecent = !!params.since_id;

  api().get('/api/v1/conversations', { params })
    .then(response => {
      const next = getLinks(response).refs.find(link => link.rel === 'next');

      dispatch(importFetchedAccounts(response.data.reduce((aggr, item) => aggr.concat(item.accounts), [])));
      dispatch(importFetchedStatuses(response.data.map(item => item.last_status).filter(x => !!x)));
      dispatch(expandConversationsSuccess(response.data, next ? next.uri : null, isLoadingRecent));
    })
    .catch(err => dispatch(expandConversationsFail(err)));
};

export const expandConversationsRequest = () => ({
  type: CONVERSATIONS_FETCH_REQUEST,
});

export const expandConversationsSuccess = (conversations, next, isLoadingRecent) => ({
  type: CONVERSATIONS_FETCH_SUCCESS,
  conversations,
  next,
  isLoadingRecent,
});

export const expandConversationsFail = error => ({
  type: CONVERSATIONS_FETCH_FAIL,
  error,
});

export const updateConversations = conversation => (dispatch, getState) => {
  dispatch(importFetchedAccounts(conversation.accounts));

  if (conversation.last_status) {
    dispatch(importFetchedStatus(conversation.last_status));
  }

  // A newly-arrived DM comes in unread; one we sent ourselves is read. On this
  // instance DMs (개인 멘션) never raise a general notification, so they'd be
  // silent - here we give an incoming DM the same boop + desktop alert a mention
  // gets, honoring the user's mention notification settings, while the sidebar
  // badge still updates via the reducer.
  const incoming = !!conversation.unread;
  const playSound = incoming && getState().getIn(['settings', 'notifications', 'sounds', 'mention'], true);
  const showAlert = incoming && getState().getIn(['settings', 'notifications', 'alerts', 'mention'], true);

  dispatch({
    type: CONVERSATIONS_UPDATE,
    conversation,
    meta: playSound ? { sound: 'boop' } : undefined,
  });

  if (showAlert && conversation.last_status && typeof window.Notification !== 'undefined' && Notification.permission === 'granted') {
    const account = conversation.accounts[0];
    const title = account ? (account.display_name.length > 0 ? account.display_name : account.username) : '';
    const body = conversation.last_status.spoiler_text.length > 0 ? conversation.last_status.spoiler_text : unescapeHTML(conversation.last_status.content);

    const notify = new Notification(title, { body, icon: account?.avatar, tag: `conversation-${conversation.id}` });
    notify.addEventListener('click', () => {
      window.focus();
      notify.close();
    });
  }
};

export const deleteConversation = conversationId => (dispatch) => {
  dispatch(deleteConversationRequest(conversationId));

  api().delete(`/api/v1/conversations/${conversationId}`)
    .then(() => dispatch(deleteConversationSuccess(conversationId)))
    .catch(error => dispatch(deleteConversationFail(conversationId, error)));
};

export const deleteConversationRequest = id => ({
  type: CONVERSATIONS_DELETE_REQUEST,
  id,
});

export const deleteConversationSuccess = id => ({
  type: CONVERSATIONS_DELETE_SUCCESS,
  id,
});

export const deleteConversationFail = (id, error) => ({
  type: CONVERSATIONS_DELETE_FAIL,
  id,
  error,
});
